import useClickOutside from 'lib/hooks/useClickOutside';
import { useState, useRef } from 'react';
import styles from './month-picker.module.scss';
import classNames from 'classnames';
import moment from 'moment';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CaretLeft,
  CaretRight,
} from '@carbon/react/icons';

export interface MonthPickerProps {
  selectedMonth?: moment.Moment | null;
  setSelectedMonth: (month: number, year: number) => void;
  onMonthChange?: (month: number, year: number) => void;
  label?: string;
  isInvalid?: boolean;
  invalidText?: string;
  light?: boolean;
  icon?: React.ReactNode;
  onIconClick?: () => void;
  disabled?: boolean;
  /** Extra class on the picker root (e.g. to give it a fixed width). */
  className?: string;
  /** Extra class on the input control (e.g. to give it a fixed height). */
  inputClassName?: string;
}

const MonthPicker = (props: MonthPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Get current date as default
  const currentMoment = moment();
  const currentMonth = currentMoment.month();
  const currentYear = currentMoment.year();

  // Use selected date if provided, otherwise use current date for display logic
  const [selectedMonth, selectedYear] = props.selectedMonth
    ? [props.selectedMonth.month(), props.selectedMonth.year()]
    : [currentMonth, currentYear];

  const [viewYear, setViewYear] = useState(selectedYear);
  const startYear = viewYear - 2;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const calendarContentRef = useRef<HTMLDivElement>(null);

  const months = [
    { id: 0, name: 'January', short: 'Jan' },
    { id: 1, name: 'February', short: 'Feb' },
    { id: 2, name: 'March', short: 'Mar' },
    { id: 3, name: 'April', short: 'Apr' },
    { id: 4, name: 'May', short: 'May' },
    { id: 5, name: 'June', short: 'Jun' },
    { id: 6, name: 'July', short: 'Jul' },
    { id: 7, name: 'August', short: 'Aug' },
    { id: 8, name: 'September', short: 'Sep' },
    { id: 9, name: 'October', short: 'Oct' },
    { id: 10, name: 'November', short: 'Nov' },
    { id: 11, name: 'December', short: 'Dec' },
  ];

  const handleSelection = (year: number, month: number) => {
    props.setSelectedMonth(month, year);
    if (props.onMonthChange) {
      props.onMonthChange(month, year);
    }
    setIsOpen(false);
  };

  useClickOutside({
    refs: [dropdownRef, calendarContentRef],
    onClickOutside: () => {
      setIsOpen(false);
    },
  });

  return (
    <div
      className={classNames(styles.container, props.className, {
        [styles.light]: props.light,
        [styles.disabled]: props.disabled,
      })}
    >
      {props.label && <label className="bx--label">{props.label}</label>}
      <div
        className={classNames(styles.input, props.inputClassName, {
          [styles.focused]: isOpen,
          [styles.invalid]: props.isInvalid,
        })}
        onClick={() => {
          if (props.disabled) return;
          // Reset view to selected month and year when opening
          if (!isOpen) {
            setViewYear(selectedYear);
            // If you have a viewMonth state, also reset it:
            // setViewMonth(selectedMonth);
          }
          setIsOpen(!isOpen);
        }}
        ref={dropdownRef}
      >
        <span className={styles.inputText}>
          {props.selectedMonth
            ? `${months[selectedMonth].name} ${selectedYear}`
            : ''}
        </span>

        {props.icon && (
          <button
            className={styles.iconButton}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              props.onIconClick?.();
            }}
          >
            {props.icon}
          </button>
        )}

        <ChevronDown className={styles.icon} />
      </div>
      {props.isInvalid && props.invalidText && (
        <div className="bx--form-requirement">{props.invalidText}</div>
      )}
      {isOpen && (
        <div className={styles.calendar} ref={calendarContentRef}>
          {/* Calendar header with navigation */}
          <div className={styles.header}>
            <button
              className={styles.navButton}
              onClick={() => setViewYear(viewYear - 5)}
              aria-label="Previous 5 years"
            >
              <CaretLeft />
            </button>

            <button
              className={styles.navButton}
              onClick={() => setViewYear(viewYear - 1)}
              aria-label="Previous year"
            >
              <ChevronLeft />
            </button>

            <span className={styles.yearIndicator}>
              {startYear} - {startYear + 4}
            </span>

            <button
              className={styles.navButton}
              onClick={() => setViewYear(viewYear + 1)}
              aria-label="Next year"
            >
              <ChevronRight />
            </button>

            <button
              className={styles.navButton}
              onClick={() => setViewYear(viewYear + 5)}
              aria-label="Next 5 years"
            >
              <CaretRight />
            </button>
          </div>

          {/* Year picker */}
          <div className={styles.yearGrid}>
            {Array.from({ length: 5 }, (_, i) => startYear + i).map((year) => (
              <button
                key={year}
                className={classNames(styles.yearButton, {
                  [styles.selected]: viewYear === year,
                })}
                onClick={() => setViewYear(year)}
              >
                {year}
              </button>
            ))}
          </div>

          {/* Month picker */}
          <div className={styles.monthGrid}>
            {months.map((month) => (
              <button
                key={month.id}
                className={classNames(styles.monthButton, {
                  [styles.selected]:
                    selectedMonth &&
                    viewYear === selectedYear &&
                    month.id === selectedMonth,
                })}
                onClick={() => handleSelection(viewYear, month.id)}
              >
                {month.short}
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <button
              className={styles.footerButton}
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthPicker;
